import { useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, ArrowPathIcon, CreditCardIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { useTerminal } from '../contexts/TerminalContext'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { useApolloClient } from '@apollo/client'
import { PaymentService } from '../lib/paymentService'
import { Payment } from '../types/order'
import { isChimeEnabled, setChimeEnabled } from '../hooks/useNewOrderChime'

interface TerminalSelectionModalProps {
  isOpen: boolean
  onClose: () => void
}

const TerminalSelectionModal: React.FC<TerminalSelectionModalProps> = ({
  isOpen,
  onClose
}) => {
  const { selectedTerminal, setSelectedTerminal, availableTerminals } = useTerminal()
  const { hasPermission } = usePermissions()
  const apolloClient = useApolloClient()
  const paymentService = new PaymentService(apolloClient)
  
  const [pendingPayments, setPendingPayments] = useState<Payment[]>([])
  const [isLoadingPendingPayments, setIsLoadingPendingPayments] = useState(false)
  const [showPaymentsOverlay, setShowPaymentsOverlay] = useState(false)
  const [isCancellingPayment, setIsCancellingPayment] = useState<number | null>(null)
  
  // Check permission - if user doesn't have permission, close modal
  useEffect(() => {
    if (isOpen && !hasPermission(PERMISSIONS.PAYMENT_TERMINAL_SELECT)) {
      onClose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPendingPayments([])
      setShowPaymentsOverlay(false)
    }
  }, [isOpen])
  
  // Don't render if user doesn't have permission
  if (!hasPermission(PERMISSIONS.PAYMENT_TERMINAL_SELECT)) {
    return null
  }

  const handleSelectTerminal = (terminal: string | null) => {
    // Use empty string to represent "None" selection (explicitly no terminal)
    // Use null for "not selected yet" (will use fallback)
    // Store empty string when user selects "None"
    setSelectedTerminal(terminal === null ? '' : terminal)
  }

  const handleFetchPayments = async (terminal: string) => {
    setIsLoadingPendingPayments(true)
    try {
      const payments = await paymentService.getPendingPaymentsByTerminal(terminal)
      setPendingPayments(payments)
      // Show overlay if payments exist
      if (payments.length > 0) {
        setShowPaymentsOverlay(true)
      }
    } catch (error) {
      setPendingPayments([])
    } finally {
      setIsLoadingPendingPayments(false)
    }
  }

  const handleCancelPayment = async (payment: Payment) => {
    if (!payment.id || !selectedTerminal || selectedTerminal === '') {
      return
    }

    setIsCancellingPayment(payment.id)
    try {
      await paymentService.cancelCardPaymentById(Number(payment.id))
      // Refresh the payments list after cancellation
      const payments = await paymentService.getPendingPaymentsByTerminal(selectedTerminal)
      setPendingPayments(payments)
      // Hide overlay if no payments left
      if (payments.length === 0) {
        setShowPaymentsOverlay(false)
      }
    } catch (error) {
      alert('Failed to cancel payment. Please try again.')
    } finally {
      setIsCancellingPayment(null)
    }
  }

  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`
  }

  return (
    <>
    <Transition appear show={isOpen}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    Select Payment Terminal
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-4 space-y-2">
                  {/* Option to deselect (no terminal) */}
                  <button
                    onClick={() => handleSelectTerminal(null)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                      selectedTerminal === ''
                        ? 'border-primary-500 bg-primary-50 text-primary-900'
                        : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                    }`}
                  >
                    <div className="font-medium">No Terminal Selected</div>
                    <div className="text-sm text-gray-500 mt-1">
                      Payments will not include a terminal ID
                    </div>
                  </button>

                  {/* Available terminals */}
                  {availableTerminals.map((terminal) => (
                    <div key={terminal} className="flex items-center gap-2">
                      <button
                        onClick={() => handleSelectTerminal(terminal)}
                        className={`flex-1 text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                          selectedTerminal === terminal
                            ? 'border-primary-500 bg-primary-50 text-primary-900'
                            : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                        }`}
                      >
                        <div className="font-medium">{terminal}</div>
                        <div className="text-sm text-gray-500 mt-1">
                          Payment Terminal ID
                        </div>
                      </button>
                      {selectedTerminal === terminal && (
                        <button
                          onClick={() => handleFetchPayments(terminal)}
                          disabled={isLoadingPendingPayments}
                          className="px-3 py-3 rounded-lg border-2 border-primary-500 bg-primary-50 text-primary-900 hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Fetch pending payments"
                        >
                          {isLoadingPendingPayments ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-900"></div>
                          ) : (
                            <ArrowPathIcon className="h-5 w-5" />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Terminal preferences */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <ChimeToggle terminalId={selectedTerminal} />
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* Pending Payments Overlay - Separate from main modal */}
    {showPaymentsOverlay && pendingPayments.length > 0 && (
      <Transition appear show={showPaymentsOverlay}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowPaymentsOverlay(false)}>
          <Transition.Child
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 flex items-center gap-2"
                    >
                      <CreditCardIcon className="h-5 w-5" />
                      Pending Payments - {selectedTerminal}
                    </Dialog.Title>
                    <button
                      onClick={() => setShowPaymentsOverlay(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {pendingPayments.map((payment) => (
                      <div
                        key={payment.id}
                        className="bg-gray-50 rounded-md p-3 border border-gray-200"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              Order #{payment.order?.order_number || payment.order_id}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              {payment.order?.customer_name || 'No customer name'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Amount: {formatPrice(payment.amount_cents)}
                            </div>
                          </div>
                          <div className="ml-3">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              {payment.status}
                            </span>
                          </div>
                        </div>
                        {payment.payment_method === 'CARD' && (payment.status === 'PENDING' || payment.status === 'UNKNOWN') && (
                          <button
                            onClick={() => handleCancelPayment(payment)}
                            disabled={isCancellingPayment === payment.id}
                            className="w-full mt-2 px-3 py-2 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {isCancellingPayment === payment.id ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                                <span>Cancelling...</span>
                              </>
                            ) : (
                              <>
                                <XCircleIcon className="h-4 w-4" />
                                <span>Cancel Payment</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPaymentsOverlay(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    )}
    </>
  )
}

// Per-terminal toggle for the "needs review" chime. Defaults to ON.
const ChimeToggle: React.FC<{ terminalId: string | null }> = ({ terminalId }) => {
  const [enabled, setEnabled] = useState<boolean>(() => isChimeEnabled(terminalId))
  useEffect(() => {
    setEnabled(isChimeEnabled(terminalId))
  }, [terminalId])

  const onToggle = (next: boolean) => {
    setEnabled(next)
    setChimeEnabled(terminalId, next)
  }

  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm font-medium text-gray-900">New-order chime</div>
        <div className="text-xs text-gray-500">
          Plays a short tone when a new HubRise order needs review.
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => onToggle(!enabled)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
          enabled ? 'bg-primary-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
            enabled ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  )
}

export default TerminalSelectionModal

